import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { WorkflowExecution } from '../types';

interface WorkflowNotificationsProps {
  workflow: WorkflowExecution | null;
  projectTitle?: string;
}

const WorkflowNotifications: React.FC<WorkflowNotificationsProps> = ({
  workflow,
  projectTitle = 'Project'
}) => {
  const toast = useRef<Toast>(null);
  const [previousStatus, setPreviousStatus] = useState<string | null>(null);
  const [previousStep, setPreviousStep] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [soundEnabled, setSoundEnabled] = useState(false);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      
      if (Notification.permission === 'default') {
        // Don't auto-request, let user enable it
        const hasAsked = localStorage.getItem('notification-permission-asked');
        if (!hasAsked) {
          toast.current?.show({
            severity: 'info',
            summary: 'Enable Notifications?',
            detail: 'Get notified when your workflow completes, even when you\'re on another tab.',
            sticky: true,
            content: (props) => (
              <div className="p-toast-message-content">
                <i className="pi pi-bell" style={{ fontSize: '2rem', marginRight: '1rem' }}></i>
                <div style={{ flex: 1 }}>
                  <div className="p-toast-summary">{props.message.summary}</div>
                  <div className="p-toast-detail">{props.message.detail}</div>
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="p-button p-button-sm p-button-success"
                      onClick={async () => {
                        const permission = await Notification.requestPermission();
                        setNotificationPermission(permission);
                        localStorage.setItem('notification-permission-asked', 'true');
                        toast.current?.clear();
                        if (permission === 'granted') {
                          toast.current?.show({
                            severity: 'success',
                            summary: 'Notifications Enabled',
                            detail: 'You\'ll receive notifications for workflow updates.',
                            life: 3000
                          });
                        }
                      }}
                    >
                      Enable
                    </button>
                    <button
                      className="p-button p-button-sm p-button-text"
                      onClick={() => {
                        localStorage.setItem('notification-permission-asked', 'true');
                        toast.current?.clear();
                      }}
                    >
                      Not Now
                    </button>
                  </div>
                </div>
              </div>
            )
          });
        }
      }
    }
  }, []);

  // Load sound preference
  useEffect(() => {
    const soundPref = localStorage.getItem('workflow-sound-enabled');
    setSoundEnabled(soundPref === 'true');
  }, []);

  // Monitor workflow status changes
  useEffect(() => {
    if (!workflow) return;

    const currentStatus = workflow.status;
    const currentStep = workflow.current_step;

    // Status changed
    if (previousStatus && previousStatus !== currentStatus) {
      handleStatusChange(previousStatus, currentStatus);
    }

    // Step changed
    if (previousStep && previousStep !== currentStep && currentStep) {
      handleStepChange(currentStep);
    }

    setPreviousStatus(currentStatus);
    setPreviousStep(currentStep || null);
  }, [workflow?.status, workflow?.current_step]);

  const handleStatusChange = (oldStatus: string, newStatus: string) => {
    // Workflow started
    if (oldStatus !== 'RUNNING' && newStatus === 'RUNNING') {
      showToast('info', 'Workflow Started', `${projectTitle} workflow is now running.`);
      showBrowserNotification('Workflow Started', `${projectTitle} workflow is now running.`);
      playSound('start');
    }

    // Workflow completed successfully
    if (newStatus === 'SUCCEEDED') {
      showToast('success', 'Workflow Completed! 🎉', `${projectTitle} workflow finished successfully.`);
      showBrowserNotification('Workflow Completed', `${projectTitle} workflow finished successfully.`);
      playSound('success');
      updatePageTitle('✅');
    }

    // Workflow failed
    if (['FAILED', 'TIMED_OUT', 'ABORTED'].includes(newStatus)) {
      const message = newStatus === 'ABORTED' ? 'cancelled' : 'failed';
      showToast('error', `Workflow ${message.charAt(0).toUpperCase() + message.slice(1)}`, `${projectTitle} workflow ${message}.`);
      showBrowserNotification(`Workflow ${message.charAt(0).toUpperCase() + message.slice(1)}`, `${projectTitle} workflow ${message}.`);
      playSound('error');
      updatePageTitle('❌');
    }
  };

  const handleStepChange = (newStep: string) => {
    const stepLabels: { [key: string]: string } = {
      'ExtractIndicators': 'Extracting Indicators',
      'DesignQuestionnaire': 'Designing Questionnaire',
      'DeployToKobo': 'Deploying to KoboToolbox'
    };

    const stepLabel = stepLabels[newStep] || newStep;
    showToast('info', 'Step Completed', `Now ${stepLabel.toLowerCase()}...`);
    updatePageTitle('⏳');
  };

  const showToast = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 5000
    });
  };

  const showBrowserNotification = (title: string, body: string) => {
    if (notificationPermission === 'granted' && 'Notification' in window) {
      // Only show if page is not visible
      if (document.hidden) {
        new Notification(title, {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'workflow-notification',
          requireInteraction: false
        });
      }
    }
  };

  const playSound = (type: 'start' | 'success' | 'error') => {
    if (!soundEnabled) return;

    // Use Web Audio API for simple notification sounds
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Different frequencies for different notification types
    const frequencies: { [key: string]: number[] } = {
      'start': [523.25, 659.25], // C5, E5
      'success': [523.25, 659.25, 783.99], // C5, E5, G5
      'error': [392.00, 329.63] // G4, E4
    };

    const freq = frequencies[type] || frequencies['start'];
    
    oscillator.frequency.value = freq[0];
    gainNode.gain.value = 0.1;

    oscillator.start(audioContext.currentTime);
    
    // Play sequence
    freq.forEach((f, i) => {
      oscillator.frequency.setValueAtTime(f, audioContext.currentTime + (i * 0.15));
    });

    oscillator.stop(audioContext.currentTime + (freq.length * 0.15));
  };

  const updatePageTitle = (emoji: string) => {
    const originalTitle = document.title.replace(/^[✅❌⏳]\s/, '');
    document.title = `${emoji} ${originalTitle}`;

    // Reset title after 5 seconds
    setTimeout(() => {
      document.title = originalTitle;
    }, 5000);
  };

  return <Toast ref={toast} position="top-right" />;
};

export default WorkflowNotifications;
